import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Logger from '../services/logger';

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  location: boolean;
  notifications: boolean;
  dataSharing: boolean;
  personalization: boolean;
}

interface ConsentContextType {
  consent: ConsentPreferences;
  updateConsent: (preferences: ConsentPreferences) => void;
  hasConsent: (type: keyof ConsentPreferences) => boolean;
  isConsentLoaded: boolean;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentPreferences>({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    location: false,
    notifications: false,
    dataSharing: false,
    personalization: false
  });

  const [isConsentLoaded, setIsConsentLoaded] = useState(false);

  // Load consent preferences from localStorage on mount
  useEffect(() => {
    const loadConsent = () => {
      try {
        const savedConsent = localStorage.getItem('tranzit-consent-preferences');
        if (savedConsent) {
          const parsedConsent = JSON.parse(savedConsent);
          setConsent(prev => ({ ...prev, ...parsedConsent }));
        }
        setIsConsentLoaded(true);
      } catch (error) {
        Logger.error('Error loading consent preferences:', error instanceof Error ? error : new Error(String(error)), { error });
        setIsConsentLoaded(true);
      }
    };

    loadConsent();
  }, []);

  // Save consent preferences to localStorage
  const updateConsent = (preferences: ConsentPreferences) => {
    try {
      localStorage.setItem('tranzit-consent-preferences', JSON.stringify(preferences));
      setConsent(preferences);
    } catch (error) {
      Logger.error('Error saving consent preferences:', error instanceof Error ? error : new Error(String(error)), { error });
    }
  };

  // Check if user has consented to a specific type
  const hasConsent = (type: keyof ConsentPreferences): boolean => {
    return consent[type] === true;
  };

  const value: ConsentContextType = {
    consent,
    updateConsent,
    hasConsent,
    isConsentLoaded
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

// Hook for checking specific consent types
export const useConsentCheck = (type: keyof ConsentPreferences): boolean => {
  const { hasConsent } = useConsent();
  return hasConsent(type);
};

// Hook for analytics consent
export const useAnalyticsConsent = (): boolean => {
  return useConsentCheck('analytics');
};

// Hook for location consent
export const useLocationConsent = (): boolean => {
  return useConsentCheck('location');
};

// Hook for notification consent
export const useNotificationConsent = (): boolean => {
  return useConsentCheck('notifications');
};

// Hook for data sharing consent
export const useDataSharingConsent = (): boolean => {
  return useConsentCheck('dataSharing');
};

// Hook for marketing consent
export const useMarketingConsent = (): boolean => {
  return useConsentCheck('marketing');
};

// Hook for personalization consent
export const usePersonalizationConsent = (): boolean => {
  return useConsentCheck('personalization');
};
